import { updateMSTObject } from 'helpers';
import { toJS } from 'mobx';
import { types as t, destroy, getParent, flow } from 'mobx-state-tree';
import { v4 as uuid4 } from 'uuid';

import { IntegrationData } from 'models/integrations';
import { LoadingState } from 'models/ui';
import { withEnv } from 'models/withEnv';

const SignetContract = t
  .model('SignetContract', {
    id: t.identifier,
    inProgress: false,
    fileUrl: t.maybeNull(t.string),
    actions: t.array(t.string),
    loading: false,
    saved: true,
    template: t.maybeNull(IntegrationData),
    cosigners: t.maybeNull(IntegrationData),
    items: t.array(IntegrationData),
    needsCosigners: true,
    quickUpload: t.maybeNull(t.string),
  })
  .volatile(() => ({
    quickUploadFile: null,
  }))
  .views(withEnv)
  .views((self) => ({
    get parent() {
      return getParent(self, 2);
    },
    get templateChoices() {
      const templates =
        self.parent.data.length && self.parent.data.find((d) => d.name === 'template');
      let templateIds = Array.from(self.parent.multipleData.keys());
      Array.from(self.parent.multipleData.values()).forEach((m) => {
        m.template && m.template.value && templateIds.push(m.template.value.toString());
      });
      return templates.choices
        .filter(
          (choice) =>
            !templateIds.includes(choice[0].toString()) ||
            (self.template && choice[0] === self.template.value),
        )
        .map((choice) => ({ value: choice[0], label: choice[1] }));
    },
    get cosignersChoices() {
      const cosigners =
        self.parent.data.length && self.parent.data.find((d) => d.name === 'cosigner');
      return cosigners.choices.map((choice) => ({ value: choice[0], label: choice[1] }));
    },
    get valueDict() {
      return self.items.reduce((agg, entry) => {
        if (entry.type === 'multiple') {
          if (entry.value) {
            agg[entry.name] = toJS(entry.value);
          } else {
            agg[entry.name] = [];
          }
        } else if (entry.type === 'repeating') {
          let groups = [];
          entry.fields.forEach((group) => {
            let fields = {};
            group.forEach((field) => {
              fields[field.name] = field.value;
            });
            groups.push(fields);
          });
          agg[entry.name] = groups;
        } else {
          agg[entry.name] = entry.value;
        }
        return agg;
      }, {});
    },
    get hasErrors() {
      return (
        self.items.some((data) => data.error !== '') ||
        self.items
          .filter((data) => data.required)
          .some((data) => data.value === '' || data.value === undefined || data.value === null) ||
        (self.needsCosigners &&
          self.cosigners &&
          (self.cosigners.value === '' ||
            self.cosigners.value === undefined ||
            self.cosigners.value === null))
      );
    },
  }))
  .actions((self) => ({
    isValid() {
      let valid = true;
      self.items.forEach((data) => {
        if (!data.valid()) {
          valid = false;
        }
      });
      return valid;
    },
    firstError() {
      let errorData = null;
      for (let i = 0; i < self.items.length; i++) {
        if (!self.items[i].valid()) {
          errorData = self.items[i];
          break;
        }
      }
      return errorData;
    },
    setId(value) {
      self.id = value;
    },
    setFile(file, b64) {
      self.quickUpload = file;
      self.quickUploadFile = b64;
      self.needsCosigners = false;
    },
    appendQuickUpload() {
      const name = self.parent.data.find((d) => d.name === 'name');
      const email = self.parent.data.find((d) => d.name === 'email');
      const ssn = self.parent.data.find((d) => d.name === 'ssn');
      const title = self.parent.data.find((d) => d.name === 'title');
      self.items = [
        {
          label: title.label,
          name: title.name,
          required: true,
          type: 'string',
          value: self.quickUpload.replace(/\.[^/.]+$/, ''),
        },
        {
          label: name.label,
          name: name.name,
          required: true,
          value: name.value,
          type: 'string',
        },
        {
          label: email.label,
          name: email.name,
          required: true,
          value: email.value,
          type: 'string',
        },
        {
          label: ssn.label,
          name: ssn.name,
          required: true,
          value: ssn.value,
          type: 'string',
        },
      ];
    },
    refetch: flow(function* () {
      self.loading = true;
      let data = self.valueDict;
      data.template = self.template.value;
      const res = yield self.parent.env.api.applications.submitIntegration(
        self.parent.applicationId,
        'applicant_hired',
        self.parent.triggerId,
        data,
        true,
      );
      if (!res.error) {
        self.items = res.data.data.data;
        self.needsCosigners = res.data.data.needsCosigners;
      } else {
        self.parent.env.showError(res.data || self.parent.env.t('integration.submit.error'));
      }
      self.loading = false;
    }),
    preview: flow(function* () {
      self.loading = true;
      let data = self.valueDict;
      data.template = self.template.value;
      if (self.quickUploadFile) {
        data.file = self.quickUploadFile;
      }
      const res = yield self.parent.env.api.applications.previewIntegration(
        self.parent.applicationId,
        'applicant_hired',
        self.parent.triggerId,
        data,
      );
      if (!res.error) {
        self.loading = false;
        return res.data;
      } else {
        self.parent.env.showError(res.data || self.parent.env.t('integration.preview.error'));
      }
      self.loading = false;
    }),
    updateRatio(value) {
      const application = self.parent.env.applicationStore.items.get(self.parent.applicationId);
      const integration = application.integrations.find(
        (i) => i.triggerId === self.parent.triggerId,
      );
      integration.setRatio(value);
    },
    submit: flow(function* (retrigger, save) {
      self.loading = true;
      let data = self.valueDict;
      data.template = self.template.value;
      if (self.cosigners.value) {
        data.cosigner = self.cosigners.value.map((v) => v.value);
      }
      if (retrigger) {
        data.retrigger = true;
      }
      if (save) {
        data.save = true;
      }
      if (self.quickUploadFile) {
        data.file = self.quickUploadFile;
      }
      const res = yield self.parent.env.api.applications.submitIntegration(
        self.parent.applicationId,
        'applicant_hired',
        self.parent.triggerId,
        data,
      );
      if (!res.error) {
        self.parent.env.showSuccess(
          save ? self.parent.env.t('integration.submit.save') : self.parent.successMessage,
        );
        self.parent.replaceMultiple(res.data.multipleData);
        self.updateRatio(res.data.ratio);
      } else {
        self.parent.env.showError(res.data || self.parent.env.t('integration.submit.error'));
      }
      self.loading = false;
    }),
    delete: flow(function* () {
      self.loading = true;
      const res = yield self.parent.env.api.applications.deleteIntegration(
        self.parent.applicationId,
        'applicant_hired',
        self.parent.triggerId,
        self.id,
      );
      if (!res.error) {
        updateMSTObject(self, res.data);
        self.updateRatio(res.data.ratio);
      } else {
        self.parent.env.showError(res.data || self.parent.env.t('integration.delete.error'));
      }
      self.loading = false;
    }),
  }));

export const SignetTrigger = t
  .model('SignetTrigger', {
    applicationId: t.number,
    triggerId: t.number,
    name: t.string,
    title: t.string,
    description: t.string,
    successMessage: t.string,
    data: t.array(IntegrationData),
    isBlocked: false,
    multipleData: t.map(SignetContract),

    // Internal, not from API
    state: LoadingState,
  })
  .views(withEnv)
  .actions((self) => ({
    afterCreate() {
      self.load();
    },
    appendMultiple() {
      const id = uuid4();
      const templates = self.data.length && self.data.find((d) => d.name === 'template');
      const cosigners = self.data.length && self.data.find((d) => d.name === 'cosigner');
      self.multipleData.put({
        id,
        template: {
          dynamic: templates.dynamic,
          label: templates.label,
          name: templates.name,
          required: templates.required,
          type: templates.type,
        },
        cosigners: {
          dynamic: cosigners.dynamic,
          label: cosigners.label,
          name: cosigners.name,
          required: cosigners.required,
          type: cosigners.type,
        },
        saved: false,
      });
    },
    removeMultiDataItem(item) {
      destroy(item);
    },
    replaceMultiple(data) {
      self.multipleData = data;
    },
    load: flow(function* () {
      if (self.state !== 'init') return;
      self.state = 'loading';
      const res = yield self.env.api.applications.loadIntegration(
        self.applicationId,
        'applicant_hired',
        self.triggerId,
      );
      if (!res.error) {
        self.state = 'loaded';
        updateMSTObject(self, res.data);
      } else {
        self.state = 'error';
      }
    }),
  }));
